import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {RedactedWorkstreamMetric, WorkstreamBasicWithSummary, WorkstreamMetric} from 'client';
import {Link} from 'components/DSYS/Link';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useCallback, useMemo} from 'react';
import {useWorkstreamDetail} from 'views/workstreams/detail/hooks';
import {useWorkstreamMetricMutation} from 'views/workstreams/hooks';
import {isFullWorkstreamMetric, isWorkstreamStatusArchived} from 'views/workstreams/utils';

import type {MetricsGrain} from './MetricsGrainControls';
import {type MetricQuarter, WorkstreamMetricsQuarterTable} from './WorkstreamMetricsQuarterTable';
import {WorkstreamMetricsSubworkstreamGroup} from './WorkstreamMetricsSubworkstreamGroup';
import {type MetricsForecastType, RedesignMetricsList} from './WorkstreamRedesignSections';

type ScorecardMetric = WorkstreamMetric | RedactedWorkstreamMetric;
const SELECTED_FORECAST_TYPE: MetricsForecastType = 'target';

const onlyMetrics = (metrics: WorkstreamBasicWithSummary['metrics'] | undefined | null) =>
  (metrics ?? []).filter((metric): metric is ScorecardMetric => metric.type === 'metric');

/**
 * Read-only metrics table for a single workstream within a list/pillar view.
 * Lazily fetches the workstream detail to surface full forecast data and direct
 * sub-workstream metrics (the list payload only carries shallow metrics).
 * Renders either the annual grid or the single-quarter scorecard projection.
 */
export const WorkstreamMetricsScorecardSection = ({
  workstream,
  selectedPeriod,
  year,
  grain,
  quarter,
  includeArchived,
}: {
  workstream: WorkstreamBasicWithSummary;
  selectedPeriod: string;
  year: number;
  grain: MetricsGrain;
  quarter: MetricQuarter;
  includeArchived: boolean;
}) => {
  const {updateMetric} = useWorkstreamMetricMutation();
  // Parent metrics (with forecasts) are already in the list payload, so render them
  // directly. Only workstreams that have direct children need a detail fetch, and
  // only to surface their sub-workstream metrics (not included in the list payload).
  const hasDependencies = (workstream.dependencies_count ?? 0) > 0;
  const {data: detail} = useWorkstreamDetail(hasDependencies ? workstream.id : undefined);

  const metrics = useMemo(() => onlyMetrics(workstream.metrics), [workstream.metrics]);

  const subWorkstreamMetricGroups = useMemo(
    () =>
      [...(detail?.dependencies ?? [])]
        .filter(({dependent}) => includeArchived || !isWorkstreamStatusArchived(dependent))
        .sort((a, b) => a.order - b.order)
        .map((dependency) => ({
          id: dependency.dependent.id,
          name: dependency.dependent.name,
          metrics: onlyMetrics(dependency.dependent.metrics),
        }))
        .filter((group) => group.metrics.length > 0),
    [detail?.dependencies, includeArchived]
  );

  const handleMetricChange = useCallback(
    async (
      workstreamId: number,
      metricList: ScorecardMetric[],
      metricId: number,
      update: Partial<WorkstreamMetric>
    ) => {
      const existingMetric = metricList.find(
        (metric): metric is WorkstreamMetric =>
          isFullWorkstreamMetric(metric) && metric.id === metricId
      );
      if (!existingMetric) return;
      await updateMetric(workstreamId, metricId, existingMetric, update);
    },
    [updateMetric]
  );

  const renderMetrics = (
    metricList: ScorecardMetric[],
    {
      workstreamId,
      workstreamName,
      hideHeader,
    }: {workstreamId: number; workstreamName: string; hideHeader: boolean}
  ) =>
    grain === 'quarterly' ? (
      <WorkstreamMetricsQuarterTable
        metrics={metricList}
        year={year}
        quarter={quarter}
        handleMetricChange={(metricId, update) =>
          handleMetricChange(workstreamId, metricList, metricId, update)
        }
        workstreamId={workstreamId}
      />
    ) : (
      <RedesignMetricsList
        workstreamId={workstreamId}
        workstreamName={workstreamName}
        metrics={metricList}
        allEditableMetrics={metricList.filter(isFullWorkstreamMetric)}
        selectedPeriod={selectedPeriod}
        selectedForecastType={SELECTED_FORECAST_TYPE}
        variant="details"
        metricPeriodOptions={[selectedPeriod]}
        showBaselineColumn
        hideHeader={hideHeader}
        disableCreateMetric
        handleMetricChange={(metricId, update) =>
          handleMetricChange(workstreamId, metricList, metricId, update)
        }
      />
    );

  if (metrics.length === 0 && subWorkstreamMetricGroups.length === 0) {
    return null;
  }

  return (
    <Box
      margin="16"
      borderTop="Solid"
      borderBottom="Solid"
      borderLeft="Solid"
      borderRight="Solid"
      borderColor="Border Subtle"
      borderRadius="Medium"
      backgroundColor="Background Base"
    >
      <Box
        position="sticky"
        padding="8"
        backgroundColor="Background Subtle"
        borderBottom="Solid"
        borderColor="Border Subtle"
        borderTopLeftRadius="Medium"
        borderTopRightRadius="Medium"
        style={{top: 0, zIndex: 1}}
      >
        <Text size="small" isBold>
          <Link
            to={`${ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(workstream.id))}`}
            monochromatic
            hasNoUnderline
            showUnderlineOnHover
          >
            {workstream.name}
          </Link>
        </Text>
      </Box>
      <Box padding="8">
        <Stack gap="16">
          {metrics.length > 0 ? (
            renderMetrics(metrics, {
              workstreamId: workstream.id,
              workstreamName: workstream.name,
              hideHeader: false,
            })
          ) : (
            <Box paddingY="8">
              <Text size="small" color="subtle">
                {t('no_metrics_yet')}
              </Text>
            </Box>
          )}
          {subWorkstreamMetricGroups.map((group) => (
            <WorkstreamMetricsSubworkstreamGroup key={group.id} name={group.name}>
              {renderMetrics(group.metrics, {
                workstreamId: group.id,
                workstreamName: group.name,
                hideHeader: true,
              })}
            </WorkstreamMetricsSubworkstreamGroup>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};
